/**
 * Course preview content hook and helpers
 *
 * Loads course copy and cover URLs from Firestore, caches ready results per tab,
 * and builds install-screen marquee columns with bundled AVIF fallback.
 *
 * References:
 * - `services/courseContentCache.ts`
 * - `utils/resolveCloudflareImagesUrlForWeb.ts`
 */
import type { Course, CourseUnit, LanguageSplitter } from '@physiofit-digital/physiofit_types';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../firebaseConfig';
import { training1WelcomeTrainingSummary } from '../components/content/assets/training1WelcomeImages';
import type { DualPhotoMarqueeImage } from '../components/content/DualPhotoMarquee';
import { resolveLanguageSplitter } from '../i18n/pickGermanText';
import {
  getCachedCourseContent,
  setCachedCourseContent,
  type CourseContentReady,
  type CourseContentResult,
} from '../services/courseContentCache';
import { resolveCloudflareImagesUrlForWeb } from '../utils/resolveCloudflareImagesUrlForWeb';

export type { CourseContentReady, CourseContentResult };

/** Required-unit count for a course (excludes the two optional weeks `Course.duration` counts). */
export const REQUIRED_COURSE_UNITS = 8;
const MAX_PRELOAD_IMAGES = 6;
const INSTALL_MARQUEE_IMAGE_ERROR_FALLBACK_THRESHOLD = 2;

const courseDocRef = (courseId: string) => doc(db, 'content', 'courses', 'courses', courseId);

const unitsCollectionRef = () => collection(db, 'content', 'courses', 'units');

export type InstallMarqueeColumns = {
  leftImages: DualPhotoMarqueeImage[];
  rightImages: DualPhotoMarqueeImage[];
  urlCount: number;
};

export type InstallMarqueeResolution = InstallMarqueeColumns & {
  imageSource: 'course' | 'fallback';
};

export type UseCourseContentOptions = {
  /** Preload hero + unit image URLs in the document head when content is ready (welcome step). */
  preloadImages?: boolean;
};

type ReadyPayload = {
  courseId: string;
  result: CourseContentResult;
};

function coverSrcFromImageAsset(
  asset: { assetUrl?: string; autoThumbnailUrl?: string } | null | undefined,
  maxWidth: 400 | 800 | 1200,
): string {
  if (!asset) {
    return '';
  }
  const url = asset.assetUrl || asset.autoThumbnailUrl;
  if (typeof url !== 'string') {
    return '';
  }
  return resolveCloudflareImagesUrlForWeb(url.trim(), maxWidth);
}

function previewBulletPointsFromCourse(course: Course): string[] {
  const points = course.patientAppPreviewBulletPoints ?? [];
  return points
    .filter((point: LanguageSplitter) => point.de !== undefined && point.de !== '')
    .map((point) => resolveLanguageSplitter(point))
    .filter((text) => text.length > 0);
}

function buildMarqueeUrlList(heroImageSrc: string, thumbnailSrcs: readonly string[]): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const src = raw.trim();
    if (!src || seen.has(src)) {
      return;
    }
    seen.add(src);
    urls.push(src);
  };
  push(heroImageSrc);
  for (const thumb of thumbnailSrcs) {
    push(thumb);
  }
  return urls;
}

function splitMarqueeImages(urls: string[]): InstallMarqueeColumns {
  const left: DualPhotoMarqueeImage[] = [];
  const right: DualPhotoMarqueeImage[] = [];
  urls.forEach((src, index) => {
    const item = { src };
    if (index % 2 === 0) {
      left.push(item);
    } else {
      right.push(item);
    }
  });
  return { leftImages: left, rightImages: right, urlCount: urls.length };
}

/**
 * Counts distinct image URLs available for marquee display.
 *
 * @param {Object} content Ready course content or hero plus thumbnails shape.
 * @return {number} Number of unique non-empty URLs.
 */
export function courseContentUrlCount(
  content: CourseContentReady | { heroImageSrc: string; thumbnailSrcs: readonly string[] },
): number {
  return buildMarqueeUrlList(content.heroImageSrc, content.thumbnailSrcs).length;
}

/**
 * Splits hero and thumbnail URLs into left and right marquee columns.
 *
 * @param {string} heroImageSrc Primary hero image URL.
 * @param {string[]} thumbnailSrcs Unit thumbnail URLs.
 * @return {Object} `leftImages`, `rightImages`, and `urlCount`.
 */
export function buildInstallMarqueeColumns(
  heroImageSrc: string,
  thumbnailSrcs: readonly string[],
): InstallMarqueeColumns {
  return splitMarqueeImages(buildMarqueeUrlList(heroImageSrc, thumbnailSrcs));
}

/**
 * Builds install marquee columns from a ready course content payload.
 *
 * @param {Object} content Ready course fetch result.
 * @return {Object} Left and right marquee image columns.
 */
export function buildInstallMarqueeColumnsFromContent(content: CourseContentReady): InstallMarqueeColumns {
  return buildInstallMarqueeColumns(content.heroImageSrc, content.thumbnailSrcs);
}

/**
 * Builds install marquee columns from bundled training-1 AVIF assets.
 *
 * @return {Object} Left and right fallback marquee columns.
 */
export function buildInstallMarqueeColumnsFromFallback(): InstallMarqueeColumns {
  return buildInstallMarqueeColumns(
    training1WelcomeTrainingSummary.heroImageSrc,
    training1WelcomeTrainingSummary.thumbnailSrcs,
  );
}

/**
 * Chooses course Firestore images or static fallback based on load status and image errors.
 *
 * @param {Object} courseContent Course hook result (loading, error, or ready).
 * @param {number} courseImageErrorCount Failed `<img>` loads on the install marquee.
 * @return {Object} Marquee columns plus `imageSource` (`course` or `fallback`).
 */
export function resolveInstallMarqueeColumns(
  courseContent: CourseContentResult,
  courseImageErrorCount: number,
): InstallMarqueeResolution {
  const useCourse =
    courseContent.status === 'ready' &&
    courseContentUrlCount(courseContent) > 0 &&
    courseImageErrorCount < INSTALL_MARQUEE_IMAGE_ERROR_FALLBACK_THRESHOLD;

  if (useCourse) {
    const columns = buildInstallMarqueeColumnsFromContent(courseContent);
    return { ...columns, imageSource: 'course' };
  }

  const columns = buildInstallMarqueeColumnsFromFallback();
  return { ...columns, imageSource: 'fallback' };
}

function preloadImageUrls(urls: readonly string[]): () => void {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  const links: HTMLLinkElement[] = [];
  const unique = [...new Set(urls.map((u) => u.trim()).filter(Boolean))].slice(0, MAX_PRELOAD_IMAGES);

  for (const href of unique) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    document.head.appendChild(link);
    links.push(link);
  }

  return () => {
    for (const link of links) {
      link.remove();
    }
  };
}

function resolvePayload(courseId: string, fetchPayload: ReadyPayload | null): ReadyPayload | null {
  if (!courseId) {
    return null;
  }
  if (fetchPayload?.courseId === courseId) {
    return fetchPayload;
  }
  const cached = getCachedCourseContent(courseId);
  if (cached) {
    return { courseId, result: cached };
  }
  return null;
}

async function fetchCourseContentFromFirestore(courseId: string): Promise<CourseContentResult> {
  const [courseSnap, unitsSnap] = await Promise.all([
    getDoc(courseDocRef(courseId)),
    getDocs(query(unitsCollectionRef(), where('courseId', '==', courseId))),
  ]);

  if (!courseSnap.exists()) {
    return { status: 'error', message: 'Kurs wurde nicht gefunden.' };
  }

  const course = courseSnap.data() as Course;
  const heroFromCourse = coverSrcFromImageAsset(course.coverImage, 800);

  const rawUnits: CourseUnit[] = [];
  unitsSnap.forEach((u) => {
    rawUnits.push({ id: u.id, ...(u.data() as Omit<CourseUnit, 'id'>) });
  });

  const unitsSorted = rawUnits
    .filter((u) => u.weekIndex < REQUIRED_COURSE_UNITS)
    .sort((a, b) => a.weekIndex - b.weekIndex);

  const unitImages = unitsSorted
    .map((u) => ({
      thumb: coverSrcFromImageAsset(u.coverImage, 400),
      hero: coverSrcFromImageAsset(u.coverImage, 800),
    }))
    .filter((p) => p.thumb.length > 0);

  const thumbnailSrcs = unitImages.map((p) => p.thumb);
  const heroImageSrcs = unitImages.map((p) => p.hero);
  const heroImageSrc = heroFromCourse || heroImageSrcs[0] || '';

  return {
    status: 'ready',
    heroImageSrc,
    thumbnailSrcs,
    heroImageSrcs,
    previewDescription: resolveLanguageSplitter(course.patientAppPreviewDescription),
    previewBulletPoints: previewBulletPointsFromCourse(course),
  };
}

/**
 * Loads course preview copy, cover, and unit thumbnails from Firestore with session cache.
 *
 * @param {string} courseId `signedUpPatient.course.id` or empty.
 * @param {Object | undefined} options Optional `preloadImages` for welcome step.
 * @return {Object} Loading, error, or ready course preview payload.
 */
export function useCourseContent(courseId: string, options?: UseCourseContentOptions): CourseContentResult {
  const trimmedId = courseId.trim();
  const preloadImages = options?.preloadImages ?? false;
  const [fetchPayload, setFetchPayload] = useState<ReadyPayload | null>(null);
  const payload = resolvePayload(trimmedId, fetchPayload);

  useEffect(() => {
    if (!trimmedId) {
      return;
    }

    if (getCachedCourseContent(trimmedId)) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      setFetchPayload(null);

      try {
        const result = await fetchCourseContentFromFirestore(trimmedId);

        if (cancelled) {
          return;
        }

        setFetchPayload({ courseId: trimmedId, result });
        setCachedCourseContent(trimmedId, result);
      } catch (error) {
        if (!cancelled) {
          console.error('useCourseContent: fetch failed', { courseId: trimmedId, error });
          const result = {
            status: 'error' as const,
            message: 'Kursdaten konnten nicht geladen werden.',
          };
          setFetchPayload({ courseId: trimmedId, result });
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [trimmedId]);

  useEffect(() => {
    if (!preloadImages || payload?.result.status !== 'ready') {
      return;
    }

    const urls = buildMarqueeUrlList(payload.result.heroImageSrc, payload.result.thumbnailSrcs);
    return preloadImageUrls(urls);
  }, [preloadImages, payload]);

  if (!trimmedId) {
    return { status: 'error', message: 'Keine Kurs-ID angegeben.' };
  }

  if (!payload) {
    return { status: 'loading' };
  }

  return payload.result;
}

/** @deprecated Use {@link useCourseContent}. */
export const useCourseContentConnect = useCourseContent;
